// components/organisms/ModalCreateNote/ModalCreateNote.tsx
import * as React from 'react';
import { useState } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useModal } from '@europrocurement/flexy-components/providers';
import { Switch } from '../../atoms';
import { NoteEditor } from '../../molecules/NoteEditor/NoteEditor';

export type ModalCreateNoteProps = {
    onConfirm: (content: string, highlighted: boolean, memoClient: boolean) => void;
    onCancel?: () => void;
    defaultMemoClient?: boolean;
    useMemoClient?: boolean;
};

export const ModalCreateNote: React.FunctionComponent<ModalCreateNoteProps> = function (props) {
    const theme = useTheme();
    const { palette, breakpoints } = theme;

    const downLgBreakpoint = useMediaQuery(breakpoints.down('lg'));
    const formWidth = downLgBreakpoint ? 'calc(100vw - 200px)' : '1098px';

    const { onConfirm, onCancel, defaultMemoClient, useMemoClient = false } = props;

    const { modalActions } = useModal();

    const [content, setContent] = useState('');
    const [highlighted, setHighlighted] = useState(false);
    const [memoClient, setMemoClient] = useState(defaultMemoClient || false);
    const [error, setError] = useState('');

    const cancel = () => {
        modalActions.close();
        if (onCancel) {
            onCancel();
        }
    };

    const confirm = () => {
        if (!content.trim()) {
            setError('Le contenu de la note est obligatoire');
            return;
        }

        modalActions.close();
        onConfirm(content.trim(), highlighted, memoClient);
    };

    const submitButtons: React.ReactNode = (
        <Box
            sx={{
                backgroundColor: palette.background.dark,
                position: 'sticky',
                left: '0',
                right: '0',
                bottom: '0',
                display: 'flex',
                flexDirection: downLgBreakpoint ? 'column' : 'row',
                justifyContent: 'center',
                gap: '16px',
                height: downLgBreakpoint ? 'auto' : '100px',
                alignItems: 'center',
                padding: downLgBreakpoint ? '20px' : '',
            }}
        >
            <Button
                onClick={cancel}
                style={{ marginTop: '8px', marginBottom: '8px' }}
                sx={{
                    color: palette.text.primary,
                    padding: '16px 24px 16px 24px',
                    width: downLgBreakpoint ? '100%' : 'auto',
                    display: downLgBreakpoint ? 'block' : 'flex',
                }}
            >
                Annuler
            </Button>
            <Button
                color="primary"
                variant="contained"
                onClick={confirm}
                disabled={!content.trim()}
                style={{ marginTop: '8px', marginBottom: '8px' }}
                sx={{
                    backgroundColor: palette.primary.main,
                    color: palette.primary.contrastText,
                    padding: '16px 24px 16px 24px',
                    width: downLgBreakpoint ? '100%' : 'auto',
                    display: downLgBreakpoint ? 'block' : 'flex',
                }}
            >
                Ajouter la note
            </Button>
        </Box>
    );

    return (
        <Box
            sx={{
                width: formWidth,
                margin: '0 auto',
            }}
        >
            <Box
                sx={{
                    width: downLgBreakpoint ? '100%' : '80%',
                    maxWidth: downLgBreakpoint ? '100%' : '800px',
                    margin: '0 auto',
                }}
            >
                <Box
                    sx={{
                        marginBottom: '10px',
                    }}
                >
                    <Typography variant="l2dh6">Ajouter une note</Typography>
                </Box>
                <Box sx={{ padding: '0 24px' }}>
                    <NoteEditor
                        value={content}
                        onChange={(value) => {
                            setContent(value);
                            if (error) setError('');
                        }}
                        error={error}
                        placeholder="Tapez votre note ici...

**Formatage disponible :**
- **Gras** avec **texte**
- *Italique* avec *texte*
- `Code` avec `texte`
- # Titre principal
- ## Sous-titre
- - Liste à puces
- 1. Liste numérotée
- > Citation
- [Lien](https://example.com)"
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mt: 2,
                        }}
                    >
                        <Typography variant="l2dbodys">Note importante</Typography>
                        <Switch
                            checked={highlighted}
                            onChange={(e) => setHighlighted(e.target.checked)}
                        />
                    </Box>
                    {useMemoClient && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                mt: 2,
                            }}
                        >
                            <Typography variant="l2dbodys">Note mémo client</Typography>
                            <Switch
                                checked={memoClient}
                                onChange={(e) => setMemoClient(e.target.checked)}
                            />
                        </Box>
                    )}
                </Box>

                {submitButtons}
            </Box>
        </Box>
    );
};
